import { assert, StringMapping, Terminator, ValueMapping } from "@opendaw/lib-std";
import { AudioUnitType, Pointers } from "@opendaw/studio-enums";
import { Devices } from "../DeviceAdapter";
import { AudioUnitTracks } from "./AudioUnitTracks";
import { AudioUnitInput } from "./AudioUnitInput";
import { IndexedBoxAdapterCollection } from "../IndexedBoxAdapterCollection";
import { ParameterAdapterSet } from "../ParameterAdapterSet";
import { AuxSendBoxAdapter } from "./AuxSendBoxAdapter";
import { AudioUnitOutput } from "./AudioUnitOutput";
export class AudioUnitBoxAdapter {
    static VolumeMapper = ValueMapping.decibel(-96.0, -9.0, +6.0);
    "class" = "device-host";
    #terminator = new Terminator();
    #context;
    #box;
    #parametric;
    #tracks;
    #input;
    #midiEffects;
    #audioEffects;
    #auxSends;
    #output;
    namedParameter; // let typescript infer the type
    constructor(context, box) {
        this.#context = context;
        this.#box = box;
        this.#parametric = this.#terminator.own(new ParameterAdapterSet(this.#context));
        this.#tracks = this.#terminator.own(new AudioUnitTracks(this, this.#context.boxAdapters));
        this.#input = this.#terminator.own(new AudioUnitInput(this.#box.input.pointerHub, this.#context.boxAdapters));
        this.#midiEffects = this.#terminator.own(IndexedBoxAdapterCollection.create(this.#box.midiEffects, box => this.#context.boxAdapters.adapterFor(box, Devices.isMidiEffect), Pointers.MidiEffectHost));
        this.#audioEffects = this.#terminator.own(IndexedBoxAdapterCollection.create(this.#box.audioEffects, box => this.#context.boxAdapters.adapterFor(box, Devices.isAudioEffect), Pointers.AudioEffectHost));
        this.#auxSends = this.#terminator.own(IndexedBoxAdapterCollection.create(this.#box.auxSends, box => this.#context.boxAdapters.adapterFor(box, AuxSendBoxAdapter), Pointers.AuxSend));
        this.#output = this.#terminator.own(new AudioUnitOutput(this.#box.output, this.#context.boxAdapters));
        this.namedParameter = this.#wrapParameters(box);
        this.#sanityCheck();
    }
    get box() { return this.#box; }
    get uuid() { return this.#box.address.uuid; }
    get address() { return this.#box.address; }
    get indexField() { return this.#box.index; }
    get type() { return this.#box.type.getValue(); }
    get captureBox() { return this.#box.capture.targetVertex; }
    get tracks() { return this.#tracks; }
    get input() { return this.#input; }
    get midiEffects() { return this.#midiEffects; }
    get audioEffects() { return this.#audioEffects; }
    get inputAdapter() { return this.#input.getValue(); }
    get auxSends() { return this.#auxSends; }
    get output() { return this.#output; }
    get isBus() { return this.input.getValue().mapOr(adapter => adapter.type === "bus", false); }
    get isInstrument() { return this.input.getValue().mapOr(adapter => adapter.type === "instrument", false); }
    get isOutput() {
        return this.#box.output.targetVertex.mapOr(output => output.box.address.equals(this.#context.rootBoxAdapter.address), false);
    }
    get midiEffectsField() { return this.#box.midiEffects; }
    get inputField() { return this.#box.input; }
    get audioEffectsField() { return this.#box.audioEffects; }
    get tracksField() { return this.#box.tracks; }
    get minimizedField() { return this.#input.getValue().unwrap().minimizedField; }
    get isAudioUnit() { return true; }
    get label() { return this.#input.getValue().mapOr(input => input.labelField.getValue(), ""); }
    audioUnitBoxAdapter() { return this; }
    indicesLimit() {
        const adapters = this.#context.rootBoxAdapter.audioUnits.adapters();
        const startIndex = this.indexField.getValue();
        const unitType = this.type;
        let min = startIndex;
        let max = startIndex;
        while (min > 0) {
            if (adapters[min - 1].type === unitType) {
                min--;
            }
            else {
                break;
            }
        }
        while (max < adapters.length - 1) {
            if (adapters[max + 1].type === unitType) {
                max++;
            }
            else {
                break;
            }
        }
        return [min, max + 1];
    }
    move(delta) { this.#context.rootBoxAdapter.audioUnits.move(this, delta); }
    moveTrack(adapter, delta) { this.#tracks.collection.move(adapter, delta); }
    deleteTrack(adapter) { this.#tracks.delete(adapter); }
    toString() { return `{${this.constructor.name}}`; }
    terminate() { this.#terminator.terminate(); }
    #wrapParameters(box) {
        return {
            volume: this.#parametric.createParameter(box.volume, AudioUnitBoxAdapter.VolumeMapper, StringMapping.decible, "volume"),
            panning: this.#parametric.createParameter(box.panning, ValueMapping.bipolar(), StringMapping.panning, "panning", 0.5),
            mute: this.#parametric.createParameter(box.mute, ValueMapping.bool, StringMapping.bool, "mute"),
            solo: this.#parametric.createParameter(box.solo, ValueMapping.bool, StringMapping.bool, "solo")
        };
    }
    #sanityCheck() {
        const address = this.address.toString();
        const capture = this.#box.capture.targetAddress.unwrapOrUndefined();
        const fail = () => `AudioUnit '${address}' must have a capture. AudioUnit is typed ${this.type} and has input ${this.#box.input.pointerHub.incoming().at(0)?.box.name}, but capture is ${capture}`;
        assert(this.type !== AudioUnitType.Instrument || this.#box.capture.targetAddress.nonEmpty(), fail);
    }
}
